import { Navigate, useOutlet } from 'react-router';

import { useAuthContext } from 'app/contexts/auth/context';
import { NoTenantScreen } from 'components/template/NoTenantScreen';
import { APP_MODULE } from 'constants/module.constant';
import { isUserAdmin } from 'hooks/useIsAdmin';

const MONITORING_MODULE = 'monitoring';

// Maps the active module slug (`VITE_REACT_APP_MODULE`, lowercased) to the
// Keycloak realm role name. By default the slug is the role; only special
// cases need an entry here.
const MODULE_TO_ROLE = {
  admin: 'administrator',
};

/**
 * Route guard that ensures the authenticated user holds the role matching
 * the active app module (e.g. `monitoring`, `industrial`, `manager`,
 * `analysis`, `admin`). Administrators bypass the check.
 *
 * In the monitoring app we redirect to the in-app `/onboarding` so the user
 * can pick (or see why they cannot pick) an app they actually have access
 * to. In the other apps (industrial, manager, analysis, admin) `/onboarding`
 * does not exist, so we render a self-contained "no module access" screen
 * with a sign-out button.
 */
export default function ModuleRoleGuard() {
  const outlet = useOutlet();
  const { user } = useAuthContext();

  const roles = (user?.roles || []).map((r) => String(r).toLowerCase());
  const isAdmin = isUserAdmin(user);
  const moduleSlug = (APP_MODULE || '').toLowerCase();
  const requiredRole = MODULE_TO_ROLE[moduleSlug] ?? moduleSlug;
  const allowed = isAdmin || !requiredRole || roles.includes(requiredRole);

  if (allowed) return <>{outlet}</>;

  if (moduleSlug === MONITORING_MODULE) {
    return <Navigate to="/onboarding" replace />;
  }

  return <NoTenantScreen reason="no-module-access" />;
}
